/**
 * Requirements & Design Card
 *
 * Single compact card consolidating thirteen study-build documents (seven
 * combined deliverables and six standalone sub-documents). Each row is one
 * line: title, description, status, and Generate / Regenerate / Download
 * action buttons. The card does NOT render document body content inline —
 * users download DOCX deliverables via the action button.
 */

import { GENERATION_LOCK_STALE_MS } from '../../../../functions/src/utils/generationWindow';
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { dataManagementDoc } from '../../constants/firestorePaths';
import { dictionaryChangedNotice, type DictionaryChangedNotice } from '../../features/dataManagement/dictionaryChangedNotice';
import { usePermission } from '../permissions/PermissionTooltip';
import RenameNotice from '../../features/renames/RenameNotice';
import { useRenameNoticeOpen } from '../../features/renames/useOpenRenameNotices';
import VerificationPanel from './VerificationPanel';
import { saveAs } from 'file-saver';
import {
  FileText,
  Download,
  Play,
  RefreshCw,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Layers,
  Component,
} from 'lucide-react';
import { displayGenerationError } from '../../utils/generationFailureText';

/* ---------- Types ---------- */

type DocGroup = 'combined' | 'standalone';

interface DocumentOption {
  id: string;
  group: DocGroup;
  title: string;
  description: string;
  generateCallable: string;
  exportCallable: string;
  /** Path under the firestore root (no leading slash). */
  firestorePath: (projectId: string) => string;
}

interface DocStatusDoc {
  generatedAt?: any;
  /** Written by the generators; the dictionary notice uses the later of the two. */
  lastModifiedAt?: any;
  _generating?: boolean;
  _generationStartedAt?: any;
  _generationError?: string;
  _generationFailedAt?: any;
  /** Written by the post-generation verification runner. Absent on documents
      generated before verification existed (and on doc types without it) —
      absence deliberately renders no chip. */
  verificationSummary?: {
    status: 'clean' | 'findings' | 'failed' | 'error';
    runId: string;
    failCount: number;
    findingsCount: number;
    completedAt?: any;
  };
}

/* ---------- Configuration ---------- */

const STALE_GENERATION_MS = GENERATION_LOCK_STALE_MS; // the lock window; shared with the backend

const DOCUMENT_OPTIONS: DocumentOption[] = [
  // ---- Combined documents (1–7) ----
  {
    id: 'urs',
    group: 'combined',
    title: 'User Requirements Specification (URS)',
    description:
      'What this study must capture and which study roles do what — combined narrative + requirements table.',
    generateCallable: 'generateURSV2',
    exportCallable: 'exportURSDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/urs`,
  },
  {
    id: 'frs',
    group: 'combined',
    title: 'Functional Requirements Specification (FRS)',
    description:
      "Functional behavior of the study's forms, edit checks, derivations, and randomization logic.",
    generateCallable: 'generateFRSV2',
    exportCallable: 'exportFRSDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/frs`,
  },
  {
    id: 'sds',
    group: 'combined',
    title: 'System Design Specification (SDS)',
    description:
      'How this study is architected inside the EDC — combined design overview.',
    generateCallable: 'generateSDSV2',
    exportCallable: 'exportSDSDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/sds`,
  },
  {
    id: 'ads',
    group: 'combined',
    title: 'Application Design Specification (ADS)',
    description:
      'Form-level design: layouts, field order, skip logic, role-based visibility.',
    generateCallable: 'generateADSV2',
    exportCallable: 'exportADSDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/ads`,
  },
  {
    id: 'atds',
    group: 'combined',
    title: 'Audit Trail Design Specification',
    description:
      'Study-specific audit-trail expectations: reason-for-change, e-signature points.',
    generateCallable: 'generateATDSV2',
    exportCallable: 'exportATDSDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/atds`,
  },
  {
    id: 'sacs',
    group: 'combined',
    title: 'Security & Access Control Specification',
    description:
      "The study's permission matrix: which study role can read/edit/sign each form and field.",
    generateCallable: 'generateSACSV2',
    exportCallable: 'exportSACSDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/sacs`,
  },
  {
    id: 'iis',
    group: 'combined',
    title: 'Interface & Integration Specification',
    description:
      "This study's external data flows: central labs, IRT, ePRO, coding dictionaries, EHR.",
    generateCallable: 'generateIISV2',
    exportCallable: 'exportIISDocxV2',
    firestorePath: (pid) => `projects/${pid}/requirements/iis`,
  },

  // ---- Standalone sub-documents (8–13) ----
  {
    id: 'sysreq',
    group: 'standalone',
    title: 'System Requirements (standalone)',
    description:
      'Standalone narrative system-requirements document. Generate when an organization wants this as its own deliverable, separate from the URS.',
    generateCallable: 'generateSystemRequirementsV2',
    exportCallable: 'exportSystemRequirementsDocxV2',
    firestorePath: (pid) => `projects/${pid}/artifacts/systemRequirements`,
  },
  {
    id: 'sysReqAttrs',
    group: 'standalone',
    title: 'System Requirements – Attributes (standalone)',
    description:
      'Standalone tabular field-level attribute spec referencing parent SR#s. 7-column table (SR# / Ver / Att No / Attribute / Acceptable Values / Codes / Comments) grouped by parent System Requirement. Generate the System Requirements document first.',
    generateCallable: 'generateSystemRequirementsAttributesV2',
    exportCallable: 'exportSystemRequirementsAttributesDocxV2',
    firestorePath: (pid) => `projects/${pid}/artifacts/systemRequirementsAttrs`,
  },
  {
    id: 'dbPhysModel',
    group: 'standalone',
    title: 'DB Physical Model (standalone)',
    description:
      'Standalone narrative description of the study database design (form-to-table mapping, visit schema, arm routing). Generate when an organization wants this as its own deliverable, separate from the SDS.',
    generateCallable: 'generateDbPhysicalModelV2',
    exportCallable: 'exportDbPhysicalModelDocxV2',
    firestorePath: (pid) => `projects/${pid}/artifacts/dbPhysicalModel`,
  },
  {
    id: 'dbAttrs',
    group: 'standalone',
    title: 'DB Attributes (standalone)',
    description:
      'Standalone tabular field-level data spec (variable, label, type, length, codelist, CDASH, SDTM). Generate when an organization wants the variable catalog as an attachment.',
    generateCallable: 'generateDbAttributesV2',
    exportCallable: 'exportDbAttributesDocxV2',
    firestorePath: (pid) => `projects/${pid}/artifacts/dbAttributes`,
  },
  {
    id: 'ifaceFeatures',
    group: 'standalone',
    title: 'Interface Features (standalone)',
    description:
      "Standalone narrative description of the study's form/screen behaviors (visit gating, skip logic, query flow, role visibility).",
    generateCallable: 'generateInterfaceFeaturesV2',
    exportCallable: 'exportInterfaceFeaturesDocxV2',
    firestorePath: (pid) => `projects/${pid}/artifacts/interfaceFeatures`,
  },
  {
    id: 'ifaceAttrs',
    group: 'standalone',
    title: 'Interface Attributes (standalone)',
    description:
      'Standalone tabular field-level UI spec (control type, required, read-only, default, skip logic, edit-check refs, help text).',
    generateCallable: 'generateInterfaceAttributesV2',
    exportCallable: 'exportInterfaceAttributesDocxV2',
    firestorePath: (pid) => `projects/${pid}/artifacts/interfaceAttributes`,
  },
];

/* ---------- Helpers (exported for reuse by sibling section cards) ---------- */

export function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `${years} yr${years === 1 ? '' : 's'} ago`;
}

export function tsToDate(ts: any): Date | null {
  if (!ts) return null;
  if (ts?.toDate) return ts.toDate();
  if (ts instanceof Date) return ts;
  return null;
}

/* ---------- Section visual config ---------- */

export interface SectionTheme {
  /** Tailwind class for the row's left accent border. */
  rowAccent: string;
  /** Tailwind classes for the section header background + text. */
  headerBg: string;
  headerText: string;
  /** Tailwind class for the section's icon. */
  iconBg: string;
  iconText: string;
}

const COMBINED_THEME: SectionTheme = {
  rowAccent: 'border-l-indigo-300',
  headerBg: 'bg-indigo-50 border-indigo-200',
  headerText: 'text-indigo-700',
  iconBg: 'bg-indigo-100',
  iconText: 'text-indigo-600',
};

const STANDALONE_THEME: SectionTheme = {
  rowAccent: 'border-l-teal-300',
  headerBg: 'bg-teal-50 border-teal-200',
  headerText: 'text-teal-700',
  iconBg: 'bg-teal-100',
  iconText: 'text-teal-600',
};

/* ---------- DocumentOptionRow ---------- */

interface DocumentOptionRowProps {
  option: DocumentOption;
  projectId: string;
  theme: SectionTheme;
  /** When false, Generate / Regenerate are disabled — every doc here is
      generated from the Data Dictionary, so until DD exists the action
      can only fail. */
  ddReady: boolean;
  /** dataManagement/dictionaryAdoption.adoptedAt, read once by the card. */
  adoptedAt: unknown;
}

function DocumentOptionRow({ option, projectId, theme, ddReady, adoptedAt }: DocumentOptionRowProps) {
  const canGenerate = usePermission('generate');
  const renameNoticeOpen = useRenameNoticeOpen(projectId, option.firestorePath(projectId));
  const [statusDoc, setStatusDoc] = useState<DocStatusDoc | null>(null);
  const [exists, setExists] = useState<boolean>(false);
  type Working = 'generate' | 'download' | null;
  const [working, setWorking] = useState<Working>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [verificationOpen, setVerificationOpen] = useState(false);

  // Subscribe to the document's status fields only
  useEffect(() => {
    if (!projectId) return;
    const path = option.firestorePath(projectId);
    const segments = path.split('/');
    const ref = doc(db, segments[0], ...segments.slice(1));
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setExists(true);
          setStatusDoc(snap.data() as DocStatusDoc);
        } else {
          setExists(false);
          setStatusDoc(null);
        }
      },
      (err) => {
        console.error(`[${option.id}] status subscription error:`, err);
      }
    );
    return () => unsub();
  }, [projectId, option.id, option]);

  // Derive presentation state
  const generatedAt = tsToDate(statusDoc?.generatedAt);
  const startedAt = tsToDate(statusDoc?._generationStartedAt);
  const isGeneratingMarker = (() => {
    if (!statusDoc?._generating) return false;
    if (!startedAt) return true;
    return Date.now() - startedAt.getTime() < STALE_GENERATION_MS;
  })();
  // Boolean (not `working === 'generate'`) so downstream comparisons of
  // `working` aren't over-narrowed by TypeScript control-flow analysis.
  const isLocallyGenerating: boolean = working === 'generate';
  const isLocallyDownloading: boolean = working === 'download';
  const showGenerating = isLocallyGenerating || isGeneratingMarker;
  const hasError = !!statusDoc?._generationError && !showGenerating;
  const isGenerated =
    exists && !!generatedAt && !showGenerating && !hasError;
  const notYet = !exists && !showGenerating && !hasError;

  /* --- Actions --- */
  const handleGenerate = async () => {
    setActionError(null);
    setWorking('generate');
    try {
      const fn = httpsCallable(functions, option.generateCallable, {
        timeout: CALLABLE_TIMEOUTS.LONG,
      });
      await fn({ projectId });
    } catch (err: any) {
      console.error(`[${option.id}] generation error:`, err);
      setActionError(err?.message || 'Generation failed');
    } finally {
      setWorking(null);
    }
  };

  const handleDownload = async () => {
    setActionError(null);
    setWorking('download');
    try {
      const fn = httpsCallable(functions, option.exportCallable);
      const result = await fn({ projectId });
      const data = result.data as {
        base64?: string;
        docx?: string;
        filename: string;
      };
      const b64 = data.base64 || data.docx;
      if (!b64) {
        throw new Error('Export response did not include document data');
      }
      const byteChars = atob(b64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(blob, data.filename || `${option.id}.docx`);
    } catch (err: any) {
      console.error(`[${option.id}] export error:`, err);
      setActionError(err?.message || 'Download failed');
    } finally {
      setWorking(null);
    }
  };

  const buttonsDisabled = showGenerating || working !== null;
  const generateBlockedReason = !canGenerate
    ? "Your role doesn't allow generating documents."
    : !ddReady
      ? 'Generate the Data Dictionary first — every Requirements & Design document is generated from it.'
      : null;
  const generateDisabled = buttonsDisabled || !ddReady || !canGenerate;

  /* --- Status badge --- */
  let badge: React.ReactNode;
  // Built from an earlier data dictionary than the adopted one (design 2026-09-14 §5).
  let dictNotice: DictionaryChangedNotice | null = null;
  if (showGenerating) {
    badge = (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
        <Loader2 className="h-3 w-3 animate-spin" />
        Generating&hellip;
      </span>
    );
  } else if (hasError) {
    badge = (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200"
        title={statusDoc?._generationError ? displayGenerationError(statusDoc._generationError) : 'Generation failed'}
      >
        <AlertCircle className="h-3 w-3" />
        Generation failed
      </span>
    );
  } else if (isGenerated && generatedAt) {
    dictNotice = dictionaryChangedNotice(adoptedAt, statusDoc, { renameNoticeOpen });
    badge = (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 border border-green-200"
        title={generatedAt.toLocaleString()}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        Generated &middot; {formatRelativeTime(generatedAt)}
      </span>
    );
  } else {
    badge = (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600 border border-gray-200">
        Not yet generated
      </span>
    );
  }

  /* --- Verification chip (builder-facing viewer of the post-generation
         verification record; absent field -> no chip, deliberately) --- */
  const summary = statusDoc?.verificationSummary;
  let verificationChip: React.ReactNode = null;
  if (isGenerated && summary) {
    const chipConfig: Record<
      string,
      { classes: string; label: string; title: string }
    > = {
      clean: {
        classes: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
        label: 'Checked ✓',
        title: 'Automatic generation check passed. Click for details.',
      },
      findings: {
        classes: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
        label: `${summary.findingsCount} finding${summary.findingsCount === 1 ? '' : 's'}`,
        title: 'Automatic generation check noted findings. Click for details.',
      },
      failed: {
        classes: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
        label: 'Check failed',
        title:
          'Automatic generation check found an inconsistency. The document is intact — click for details.',
      },
      error: {
        classes: 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200',
        label: 'Not checked',
        title: 'The automatic check could not run. Click for details.',
      },
    };
    const cfg = chipConfig[summary.status];
    if (cfg) {
      verificationChip = (
        <button
          type="button"
          onClick={() => setVerificationOpen((v) => !v)}
          title={cfg.title}
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${cfg.classes}`}
        >
          {cfg.label}
        </button>
      );
    }
  }

  /* --- Action buttons --- */
  const actionButtons: React.ReactNode[] = [];

  if (notYet || hasError) {
    if (!canGenerate) {
      // Read-only role: replace the Generate CTA with a hint so the
      // user understands the document hasn't been built yet rather
      // than seeing a disabled button they can't act on.
      actionButtons.push(
        <span
          key="gen"
          className="inline-flex items-center h-8 px-3 rounded-md bg-slate-50 text-slate-600 text-xs font-medium ring-1 ring-inset ring-slate-200"
        >
          Not yet generated
        </span>
      );
    } else {
      actionButtons.push(
        <button
          key="gen"
          type="button"
          onClick={handleGenerate}
          disabled={generateDisabled}
          title={generateBlockedReason || 'Generate this document'}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLocallyGenerating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4 fill-white" />
          )}
          <span>Generate</span>
        </button>
      );
    }
  }

  if (isGenerated) {
    actionButtons.push(
      <button
        key="dl"
        type="button"
        onClick={handleDownload}
        disabled={buttonsDisabled}
        title="Download DOCX"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLocallyDownloading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        <span>Download</span>
      </button>
    );
    actionButtons.push(
      <button
        key="regen"
        type="button"
        onClick={handleGenerate}
        disabled={generateDisabled}
        title={generateBlockedReason || 'Regenerate'}
        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        {isLocallyGenerating ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        )}
      </button>
    );
  }

  if (showGenerating && actionButtons.length === 0) {
    actionButtons.push(
      <span
        key="spinner"
        className="inline-flex items-center justify-center h-8 w-8 text-gray-400"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </span>
    );
  }

  return (
    <div
      className={`rounded-lg border-l-4 ${theme.rowAccent} hover:bg-gray-50 transition-colors`}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div
          className={`h-7 w-7 rounded-md flex items-center justify-center ${theme.iconBg} ${theme.iconText} flex-shrink-0`}
        >
          <FileText className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {option.title}
          </div>
          <p className="text-xs text-gray-500 truncate" title={option.description}>
            {option.description}
          </p>
          {actionError && (
            <p className="text-[11px] text-red-600 mt-0.5 truncate" title={actionError}>
              {actionError}
            </p>
          )}
          {dictNotice && (
            <p className="text-[11px] text-amber-700 mt-0.5">{dictNotice.text}</p>
          )}
          {isGenerated && (
            <RenameNotice
              compact
              projectId={projectId}
              documentPath={option.firestorePath(projectId)}
              onRegenerate={handleGenerate}
              regenerating={isLocallyGenerating}
            />
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {verificationChip}
          {badge}
          <div className="flex items-center gap-1">{actionButtons}</div>
        </div>
      </div>
      {verificationOpen && summary && (
        <VerificationPanel
          docPath={option.firestorePath(projectId)}
          runId={summary.runId}
          onClose={() => setVerificationOpen(false)}
        />
      )}
    </div>
  );
}

/* ---------- Section block ---------- */

interface SectionBlockProps {
  /** ID used for stable React keys; not visible. */
  sectionId: string;
  title: string;
  subtitle?: string;
  count: number;
  icon: React.ReactNode;
  theme: SectionTheme;
  expanded: boolean;
  onToggle: () => void;
  options: DocumentOption[];
  projectId: string;
  ddReady: boolean;
  adoptedAt: unknown;
}

function SectionBlock({
  title,
  subtitle,
  count,
  icon,
  theme,
  expanded,
  onToggle,
  options,
  projectId,
  ddReady,
  adoptedAt,
}: SectionBlockProps) {
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border ${theme.headerBg} hover:opacity-90 transition-colors`}
      >
        {expanded ? (
          <ChevronDown className={`h-4 w-4 ${theme.headerText}`} />
        ) : (
          <ChevronRight className={`h-4 w-4 ${theme.headerText}`} />
        )}
        <div
          className={`h-8 w-8 rounded-lg flex items-center justify-center ${theme.iconBg} ${theme.iconText}`}
        >
          {icon}
        </div>
        <div className="flex-1 text-left">
          <div className={`text-sm font-semibold ${theme.headerText}`}>
            {title}
          </div>
          {subtitle && (
            <p className="text-xs text-gray-600 mt-0.5">{subtitle}</p>
          )}
        </div>
        <span
          className={`text-xs font-semibold ${theme.headerText} px-2 py-0.5 rounded-full bg-white/60`}
        >
          {count} {count === 1 ? 'document' : 'documents'}
        </span>
      </button>

      {expanded && (
        <div className="space-y-1 pl-2">
          {options.map((option) => (
            <DocumentOptionRow
              key={option.id}
              option={option}
              projectId={projectId}
              theme={theme}
              ddReady={ddReady}
              adoptedAt={adoptedAt}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- RequirementsDesignCard ---------- */

interface RequirementsDesignCardProps {
  projectId: string;
}

export default function RequirementsDesignCard({
  projectId,
}: RequirementsDesignCardProps) {
  const combined = DOCUMENT_OPTIONS.filter((o) => o.group === 'combined');
  const standalone = DOCUMENT_OPTIONS.filter((o) => o.group === 'standalone');

  const [combinedExpanded, setCombinedExpanded] = useState(true);
  const [standaloneExpanded, setStandaloneExpanded] = useState(true);

  // Watch the Data Dictionary doc — every document in this card is
  // generated from it, so until DD exists the Generate buttons are
  // disabled with an explanatory tooltip.
  const [ddReady, setDdReady] = useState(false);
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      dataManagementDoc(projectId, 'dataDictionary'),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setDdReady(
          !!data &&
            (typeof data.fieldCount === 'number'
              ? data.fieldCount > 0
              : true)
        );
      }
    );
    return () => unsub();
  }, [projectId]);

  // One read of the adoption record for every row: a row built from an earlier
  // dictionary than the adopted one says to regenerate it (design 2026-09-14 §5).
  const [adoption, setAdoption] = useState<{ adoptedAt?: unknown } | null>(null);
  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      dataManagementDoc(projectId, 'dictionaryAdoption'),
      (snap) => {
        setAdoption(snap.exists() ? snap.data() : null);
      },
      (err) => {
        console.error('[reqDesign] dictionary adoption subscription error:', err);
      }
    );
    return () => unsub();
  }, [projectId]);

  return (
    <div className="card p-5 space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Requirements &amp; Design
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Generate the documents your organization needs as deliverables.
        </p>
      </div>

      {!ddReady && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Generate the Data Dictionary in <strong>Study Documents</strong> before
          generating the documents below — every document in this card is built
          from it.
        </p>
      )}

      <SectionBlock
        sectionId="combined"
        title="Combined documents"
        subtitle="Full deliverables that bundle related requirements or design content into one document."
        count={combined.length}
        icon={<Layers className="h-4 w-4" />}
        theme={COMBINED_THEME}
        expanded={combinedExpanded}
        onToggle={() => setCombinedExpanded((v) => !v)}
        options={combined}
        projectId={projectId}
        ddReady={ddReady}
        adoptedAt={adoption?.adoptedAt}
      />

      <SectionBlock
        sectionId="standalone"
        title="Standalone sub-documents"
        subtitle="Individual attachments. Generate any one without producing its larger combined document."
        count={standalone.length}
        icon={<Component className="h-4 w-4" />}
        theme={STANDALONE_THEME}
        expanded={standaloneExpanded}
        onToggle={() => setStandaloneExpanded((v) => !v)}
        options={standalone}
        projectId={projectId}
        ddReady={ddReady}
        adoptedAt={adoption?.adoptedAt}
      />
    </div>
  );
}
